import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, ArrowUp, ArrowDown, Trash2, Star, Home, Edit } from 'lucide-react';
import { EditProjectForm } from './EditProjectForm';
import { useToast } from '@/hooks/use-toast';

interface Project {
  id: string;
  title: string;
  description: string;
  location: string;
  status: string;
  featured: boolean;
  project_images: { 
    id: string;
    image_url: string; 
    alt_text: string;
    caption: string;
    sort_order: number;
  }[];
  project_category_assignments: { 
    project_categories: { id: string; name: string } 
  }[];
}

interface FeaturedProject {
  id: string;
  project_id: string;
  display_order: number;
  is_active: boolean;
  city_override?: string;
  projects: Project;
}

export const HomepageFeaturedManager = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [featuredProjects, setFeaturedProjects] = useState<FeaturedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all published projects
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          *,
          project_images (
            id,
            image_url,
            alt_text,
            caption,
            sort_order
          ),
          project_category_assignments (
            project_categories (id, name)
          )
        `)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;

      // Load featured projects
      const { data: featuredData, error: featuredError } = await supabase
        .from('featured_projects')
        .select(`
          *,
          projects (
            *,
            project_images (
              id,
              image_url,
              alt_text,
              caption,
              sort_order
            ),
            project_category_assignments (
              project_categories (id, name)
            )
          )
        `)
        .eq('is_active', true)
        .order('display_order');

      if (featuredError) throw featuredError;

      setProjects(projectsData || []);
      setFeaturedProjects(featuredData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load projects data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addFeaturedProject = async () => {
    if (!selectedProjectId) return;

    try {
      const nextOrder = Math.max(...featuredProjects.map(fp => fp.display_order), 0) + 1;
      
      const { error } = await supabase
        .from('featured_projects')
        .insert({
          project_id: selectedProjectId,
          display_order: nextOrder,
          is_active: true
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project added to homepage featured section",
      });

      setSelectedProjectId('');
      loadData();
    } catch (error) {
      console.error('Error adding featured project:', error);
      toast({
        title: "Error",
        description: "Failed to add featured project",
        variant: "destructive",
      });
    }
  };

  const removeFeaturedProject = async (id: string) => {
    try {
      const { error } = await supabase
        .from('featured_projects')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project removed from homepage",
      });

      loadData();
    } catch (error) {
      console.error('Error removing featured project:', error);
      toast({
        title: "Error",
        description: "Failed to remove featured project",
        variant: "destructive",
      });
    }
  };

  const moveProject = async (id: string, direction: 'up' | 'down') => {
    const currentIndex = featuredProjects.findIndex(fp => fp.id === id);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= featuredProjects.length) return;

    try {
      const current = featuredProjects[currentIndex];
      const target = featuredProjects[newIndex];

      // Swap display orders
      await Promise.all([
        supabase.from('featured_projects').update({ display_order: target.display_order }).eq('id', current.id),
        supabase.from('featured_projects').update({ display_order: current.display_order }).eq('id', target.id)
      ]);

      toast({
        title: "Success",
        description: "Project order updated",
      });

      loadData();
    } catch (error) {
      console.error('Error moving project:', error);
      toast({
        title: "Error",
        description: "Failed to update project order",
        variant: "destructive",
      });
    }
  };

  const availableProjects = projects.filter(
    project => !featuredProjects.some(fp => fp.project_id === project.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Info Banner */}
      <Card className="border-l-4 border-l-primary">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Home className="h-5 w-5" />
            Homepage Featured Projects
          </CardTitle>
          <CardDescription className="text-sm">
            These 3 projects appear prominently in the "Recent Projects" section on your homepage and city landing pages. They are the first thing visitors see.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Add New Featured Project */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
            Add Featured Project
          </CardTitle>
          <CardDescription className="text-sm">
            Select a project to feature prominently on your homepage (limit: 3 projects)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger className="flex-1 min-h-[48px] text-sm">
                <SelectValue placeholder="Select a project to feature..." />
              </SelectTrigger>
              <SelectContent>
                {availableProjects.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground">No available projects</div>
                ) : (
                  availableProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                        <span className="font-medium">{project.title}</span>
                        <Badge variant="secondary" className="text-xs w-fit">
                          {project.location}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button 
              onClick={addFeaturedProject} 
              disabled={!selectedProjectId || featuredProjects.length >= 3} 
              className="min-h-[48px] w-full sm:w-auto sm:min-w-[100px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add to Homepage
            </Button>
          </div>
          {featuredProjects.length >= 3 && (
            <p className="text-xs text-muted-foreground mt-2">
              Maximum of 3 featured projects reached. Remove one to add another.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Current Featured Projects */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base sm:text-lg">
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 sm:h-5 sm:w-5" />
              Homepage Featured ({featuredProjects.length}/3)
            </div>
          </CardTitle>
          <CardDescription className="text-sm">
            Manage the order and selection of projects featured on your homepage
          </CardDescription>
        </CardHeader>
        <CardContent>
          {featuredProjects.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Star className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-sm">No featured projects yet.</p>
              <p className="text-xs">Add some projects above to showcase on your homepage.</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {featuredProjects.map((featured, index) => (
                <div key={featured.id} className="flex flex-col gap-3 p-3 sm:p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                  {/* Mobile Layout */}
                  <div className="flex items-center justify-between sm:hidden">
                    <div className="flex items-center gap-3">
                      {featured.projects.project_images.length > 0 && (
                        <img
                          src={featured.projects.project_images[0].image_url}
                          alt={featured.projects.project_images[0].alt_text || featured.projects.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}
                      <div>
                        <h4 className="font-semibold text-sm">{featured.projects.title}</h4>
                        <p className="text-xs text-muted-foreground">{featured.projects.location}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      #{index + 1}
                    </Badge>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden sm:flex items-center gap-4">
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => moveProject(featured.id, 'up')}
                        disabled={index === 0}
                        className="min-h-[36px] min-w-[36px]"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => moveProject(featured.id, 'down')}
                        disabled={index === featuredProjects.length - 1}
                        className="min-h-[36px] min-w-[36px]"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="flex items-center gap-4 flex-1">
                      {featured.projects.project_images.length > 0 && (
                        <img
                          src={featured.projects.project_images[0].image_url}
                          alt={featured.projects.project_images[0].alt_text || featured.projects.title}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold">{featured.projects.title}</h4>
                        <p className="text-sm text-muted-foreground">{featured.projects.location}</p>
                        <Badge variant="outline" className="text-xs mt-1">
                          Position #{index + 1}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedProject(featured.projects)}
                            className="min-h-[36px]"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto m-2 sm:m-6">
                          <DialogHeader>
                            <DialogTitle>Edit Homepage Featured Project</DialogTitle>
                          </DialogHeader>
                          {selectedProject && (
                            <EditProjectForm
                              project={{
                                ...selectedProject,
                                status: selectedProject.status as 'draft' | 'published',
                                project_images: selectedProject.project_images.map(img => ({
                                  id: img.id,
                                  image_url: img.image_url,
                                  alt_text: img.alt_text,
                                  caption: img.caption,
                                  sort_order: img.sort_order
                                })),
                                project_category_assignments: selectedProject.project_category_assignments.map(assignment => ({
                                  project_categories: { 
                                    id: assignment.project_categories.id,
                                    name: assignment.project_categories.name 
                                  }
                                }))
                              }}
                              onSuccess={() => {
                                setIsEditDialogOpen(false);
                                loadData();
                              }}
                            />
                          )}
                        </DialogContent>
                      </Dialog>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeFeaturedProject(featured.id)}
                        className="min-h-[36px]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Mobile Controls */}
                  <div className="flex gap-2 sm:hidden">
                    <div className="flex gap-1 flex-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => moveProject(featured.id, 'up')}
                        disabled={index === 0}
                        className="min-h-[44px] flex-1"
                      >
                        <ArrowUp className="h-4 w-4 mr-1" />
                        Up
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => moveProject(featured.id, 'down')}
                        disabled={index === featuredProjects.length - 1}
                        className="min-h-[44px] flex-1"
                      >
                        <ArrowDown className="h-4 w-4 mr-1" />
                        Down
                      </Button>
                    </div>
                    
                    <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedProject(featured.projects)}
                          className="min-h-[44px] px-4"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto m-2 sm:m-6">
                        <DialogHeader>
                          <DialogTitle>Edit Homepage Featured Project</DialogTitle>
                        </DialogHeader>
                        {selectedProject && (
                          <EditProjectForm
                            project={{
                              ...selectedProject,
                              status: selectedProject.status as 'draft' | 'published',
                              project_images: selectedProject.project_images.map(img => ({
                                id: img.id,
                                image_url: img.image_url,
                                alt_text: img.alt_text,
                                caption: img.caption,
                                sort_order: img.sort_order
                              })),
                              project_category_assignments: selectedProject.project_category_assignments.map(assignment => ({
                                project_categories: { 
                                  id: assignment.project_categories.id,
                                  name: assignment.project_categories.name 
                                }
                              }))
                            }}
                            onSuccess={() => {
                              setIsEditDialogOpen(false);
                              loadData();
                            }}
                          />
                        )}
                      </DialogContent>
                    </Dialog>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeFeaturedProject(featured.id)}
                      className="min-h-[44px] px-4"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      {featuredProjects.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Homepage Preview</CardTitle>
            <CardDescription className="text-sm">
              This is how the featured projects will appear on your website
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {featuredProjects.slice(0, 3).map((featured, index) => (
                <div key={featured.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  {featured.projects.project_images.length > 0 && (
                    <img
                      src={featured.projects.project_images[0].image_url}
                      alt={featured.projects.project_images[0].alt_text || featured.projects.title}
                      className="w-full h-32 sm:h-36 object-cover"
                    />
                  )}
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <h5 className="font-semibold text-sm">{featured.projects.title}</h5>
                      <Badge variant="outline" className="text-xs">#{index + 1}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{featured.projects.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};